"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "How does revenue management improve my hotel's profitability?",
    answer: "Revenue management uses data-driven strategies to optimize pricing and inventory. By analyzing market demand, competitor rates, and historical booking patterns, we ensure your rooms are sold at the right price to the right guest at the right time, significantly increasing your RevPAR."
  }, {
    question: "What is the role of OTA optimization in my revenue strategy?",
    answer: "OTAs are critical discovery channels. We optimize your listings with high-converting content, professional imagery, and strategic rate parity management to improve your visibility, ranking, and conversion rates on platforms like Booking.com and Expedia."
  }, {
    question: "Do you provide support for hotel technology implementation?",
    answer: "Yes. We specialize in auditing and recommending the right hospitality tech stack—from Channel Managers and PMS to Booking Engines—ensuring seamless integration that automates operations and drives direct bookings."
  }, {
    question: "How do you handle rate parity and distribution?",
    answer: "We monitor your rates across all channels in real-time. Our strategy focuses on maintaining strict rate parity to protect your brand integrity while incentivizing direct bookings through exclusive offers and loyalty benefits."
  }, {
    question: "What is included in a hotel revenue audit?",
    answer: "Our comprehensive audit evaluates your current pricing strategy, distribution channel performance, tech stack efficiency, and market positioning. We provide actionable insights to identify revenue leakage and growth opportunities."
  }, {
    question: "Can you help with digital marketing for hotels?",
    answer: "Absolutely. We integrate revenue management with digital marketing, including SEO, paid media, and social strategy, to ensure your hotel's online presence attracts high-intent guests and drives profitable direct traffic."
  }, {
    question: "How do you approach dynamic pricing for independent hotels?",
    answer: "We implement sophisticated dynamic pricing models tailored to your property's unique needs. By monitoring real-time market fluctuations and local events, we adjust your rates automatically to capture maximum demand."
  }, {
    question: "What makes HotelRevBaBa different from other consultancies?",
    answer: "Led by Sunil Singh, we combine deep technical expertise with a hands-on, personalized approach. We don't just provide reports; we act as an extension of your team to execute strategies that deliver measurable revenue growth."
  }, {
    question: "Do you work with small boutique hotels or large properties?",
    answer: "We work with a diverse range of properties, from independent boutique hotels to larger resorts. Our strategies are scalable and customized to fit the specific operational and financial goals of your property."
  }, {
    question: "How can I get started with a consultation?",
    answer: "Simply fill out our contact form or call us directly. We'll schedule an initial discovery call to understand your current challenges and discuss how our revenue management solutions can help you achieve your goals."
  }];
  return <section data-section-id="701" id="faq" className="bg-slate-50 py-20 md:py-32">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <span className="inline-block text-sm font-semibold uppercase tracking-widest text-blue-700 mb-6">
                  FAQ
                </span>
                <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] font-bold leading-[1.05] tracking-tight text-slate-900 mb-6">
                  Expert Revenue Insights.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-10 max-w-md">
                  Get clear answers to your hospitality revenue management questions and learn how we help hotels maximize profitability.
                </p>

                {}
                <div className="bg-white rounded-lg p-6 shadow-md border border-slate-200 mb-6">
                  <div className="flex items-start gap-4">
                    <SafeHydrate><Avatar className="size-12 shrink-0">
                      <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_consultant.png" alt="Sunil Singh, Hotel Revenue Management Expert" />
                      <AvatarFallback className="bg-blue-600 text-white font-semibold">
                        SS
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div className="flex-1">
                      <p className="font-default font-semibold text-slate-900 mb-1">
                        Need personalized advice?
                      </p>
                      <p className="text-sm text-slate-600 mb-3">
                        Speak with Sunil Singh about your property's revenue potential.
                      </p>
                      <Link to="/ContactPage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800 transition-colors group">
                        Contact our team
                        <ArrowRight className="size-4 text-blue-600 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>

                {}
                <Button asChild size="lg" className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg">
                  <Link to="tel:+919999999999">
                    <Phone className="size-4 mr-2" />
                    Call for Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-slate-200 last:border-b-0 group/item data-[state=open]:border-l-[3px] data-[state=open]:border-l-blue-600 data-[state=open]:pl-6 transition-all duration-300">
                  <AccordionTrigger className="py-6 hover:no-underline [&>svg]:hidden group">
                    <span className="font-default text-lg md:text-xl font-semibold text-slate-900 text-left pr-6 leading-snug">
                      {faq.question}
                    </span>
                    <span aria-hidden="true" className="relative flex size-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white group-data-[state=open]:bg-blue-600 group-data-[state=open]:text-white">
                      <span className="absolute h-0.5 w-4 bg-current rounded" />
                      <span className="absolute h-0.5 w-4 bg-current rounded transition-transform duration-300 group-data-[state=open]:rotate-90 group-data-[state=closed]:rotate-0 rotate-90" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-12">
                    <p className="text-base text-slate-600 leading-[1.7]">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}